import { inject, Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { BehaviorSubject, catchError, delay, filter, first, map, Observable, of, switchMap, tap } from 'rxjs';
import { ClaimTypes } from 'src/app/auth/auth.constants';
import { IDictionary, ILoginResult, IOidcCustomParams, Language, Logs, settings$ } from 'src/app/core/contracts';
import { formattedStringify, styledLog } from 'src/app/core/helpers/logger';
import { UI } from 'src/app/core/helpers/ui';
import { environment } from 'src/environments/environment';

export const authLog = (msg: string, params?: unknown, style: Record<string, string> = { 'background-color': '#339989', color: 'white' }) => {
  if (Logs.ENABLE_AUTH_FLOW_LOGS) {
    try {
      styledLog({ msg, params: params ? formattedStringify(params) : undefined }, style);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('@authLog: Failed to log message', err);
    }
  }
};

/**
 * Main authentication service that handles both mobile and web authentication flows.
 * This service automatically selects the appropriate authentication based on the current platform (Capacitor for mobile, web for browser)
 */
@Injectable({
  providedIn: 'root',
})
export class AuthHandler {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly eventService = inject(PublicEventsService);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly ui = inject(UI);

  private handler: BasePlatformAuth;

  constructor() {
    this.handler = new WebPlatformAuth(this.oidcSecurityService, this.eventService, this.zone, this.router, this.ui);
  }
  get isReady$(): Observable<boolean> {
    return this.handler.isReady$;
  }
  checkAuth(url?: string) {
    return this.handler.checkAuth(url);
  }
  login(username?: string): Observable<ILoginResult | null> {
    return this.handler.login(username);
  }
  logout(): Observable<unknown> {
    return this.handler.logout();
  }
  getRoles(forceUpdate?: boolean) {
    return this.handler.getRoles(forceUpdate);
  }
}

abstract class BasePlatformAuth {
  protected isReady = new BehaviorSubject<boolean>(false);

  constructor(
    protected oidcSecurityService: OidcSecurityService,
    protected eventService: PublicEventsService,
    protected zone: NgZone,
    protected router: Router,
    protected ui: UI
  ) {
    this.init();
  }
  abstract login(username?: string): Observable<ILoginResult>;
  abstract logout(): Observable<unknown>;
  get isReady$(): Observable<boolean> {
    return this.isReady.asObservable();
  }
  init() {
    this.eventService
      .registerForEvents()
      .pipe(filter(notification => notification.type === EventTypes.CheckingAuthFinished || notification.type === EventTypes.CheckingAuthFinishedWithError))
      .subscribe(() => {
        authLog(`auth.flow::=>eventService: CheckingAuthFinished. isReady.set 'true'`);
        this.isReady.next(true);
      });

    if (Logs.ENABLE_AUTH_FLOW_LOGS) {
      this.router.events.pipe(filter(ev => ev instanceof NavigationEnd)).subscribe(ev => {
        authLog(`auth.flow::=>NavigationEnd: `, { url: ev?.url, urlAfterRedirects: ev?.urlAfterRedirects }, { 'background-color': '#E49EDD', color: 'black' });
      });
    }
  }
  checkAuth(url?: string) {
    if (url) {
      // url is provided => it's a redirection step from auth server
      authLog(`auth.flow::=>auth.handler.checkAuth: checking auth with url`, { url });
      return this.oidcSecurityService.checkAuth(url);
    }
    // no url provided => it's app startup or web, access token may already be expired
    return this.oidcSecurityService.checkAuth().pipe(
      switchMap(response => {
        if (response?.isAuthenticated) {
          authLog(`auth.flow::=>auth.handler.checkAuth: checked auth without url and found an active session`, {
            ...response,
            accessToken: '...',
            idToken: '...',
          });
          return of(response);
        }
        authLog(`auth.flow::=>auth.handler.checkAuth: user is not authenticated, checking refresh token`, { ...response, accessToken: '...', idToken: '...' });
        return this.oidcSecurityService.getRefreshToken().pipe(
          switchMap(refreshToken => {
            if (!refreshToken) {
              authLog(`auth.flow::=>auth.handler.checkAuth: no refresh token available`);
              return of(response);
            }
            authLog(`auth.flow::=>auth.handler.checkAuth: found a refresh token, attempting to refresh session`, { refreshToken });
            return this.oidcSecurityService.forceRefreshSession().pipe(
              tap(refreshResponse => {
                if (refreshResponse?.isAuthenticated) {
                  authLog(`auth.flow::=>auth.handler.checkAuth: session refreshed successfully with refresh token`, {
                    ...refreshResponse,
                    accessToken: '...',
                    idToken: '...',
                  });
                } else {
                  authLog(`auth.flow::=>auth.handler.checkAuth: failed to refresh session with refresh token`, {
                    ...refreshResponse,
                    accessToken: '...',
                    idToken: '...',
                  });
                }
              }),
              catchError(err => {
                authLog(`auth.flow::=>auth.handler.checkAuth: failed to refresh session with refresh token`);
                return of(null);
              })
            );
          })
        );
      })
    );
  }
  getCustomParams(username?: string): Observable<IOidcCustomParams> {
    return settings$.pipe(
      first(),
      map(settings => {
        // eslint-disable-next-line no-debugger
        debugger;
        const language = Language[settings?.language || Language.en];
        return username ? { username, ui_locales: language, version: environment.version } : { ui_locales: language, version: environment.version };
      })
    );
  }
  getRoles(forceUpdate?: boolean) {
    let stream = this.oidcSecurityService.getPayloadFromAccessToken();
    if (forceUpdate) {
      /* claims in the access token are immutable, to change them the user would have to reauthenticate
         so we rely on the user data endpoint to get the latest claims */
      authLog(`auth.flow::=>getRoles: force update requested`);
      stream = this.oidcSecurityService.getUserData();
    }
    return stream.pipe(
      map((claims: IDictionary<unknown>) => {
        authLog(`auth.flow::=>getRoles:`, { claims });
        if (!claims) {
          return [];
        }
        let roles = (claims[ClaimTypes.Role] || []) as string | string[]; // || in case there are no claims
        if (!Array.isArray(roles)) {
          // if there's only one claim
          roles = [roles.toLowerCase()];
        }
        roles = roles.map(i => i.toLowerCase());
        return roles;
      })
    );
  }
}

/**
 * Web-specific authentication implementation using standard browser redirects
 */
class WebPlatformAuth extends BasePlatformAuth {
  login(username?: string): Observable<ILoginResult | null> {
    this.ui.showLoading();
    return this.getCustomParams(username).pipe(
      tap(customParams => this.oidcSecurityService.authorize(undefined, { customParams })),
      delay(300000), // on web the consumer won't receive the response since the page will get redirected to the auth server
      map(() => null as ILoginResult)
    );
  }
  logout(): Observable<unknown> {
    this.ui.showLoading();
    /* will logout the user locally and clear session data, then navigates to the logout url
     once the logout on the auth server is completed the user will be redirected to the postLogoutRedirectUrl i.e: main route '/' */
    return this.oidcSecurityService.logoffAndRevokeTokens();
  }
}
